import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Send, Sparkles } from "lucide-react";
import Logo from "../components/app/Logo";
import RunLimitDialog from "../components/app/RunLimitDialog";
import { api, formatApiErrorDetail } from "../lib/api";

// How often to poll a running generation job. The pipeline reports progress once per
// wave, so polling much faster than this just adds requests without new information.
const GENERATION_POLL_MS = 2000;

const LOADING_MESSAGES = [
  "Analyzing your requirements...",
  "Selecting recommended services...",
  "Mapping your architecture...",
  "Estimating costs...",
  "Finalizing your blueprint...",
];
const GENERATION_STAGES = [
  { key: "planning", label: "Plan the services" },
  { key: "network", label: "Design network and security" },
  { key: "costs", label: "Review costs and architecture" },
  { key: "access", label: "Define access controls" },
];

/* ------------------------------------------------------------------ *
 *  Phase 1 — Name the project
 * ------------------------------------------------------------------ */
function NamingScreen({ onSubmit, prefillFromTemplate }) {
  const [name, setName] = useState(prefillFromTemplate ? `${prefillFromTemplate}` : "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Give your project a name to continue.");
      return;
    }
    if (trimmed.length > 60) {
      setError("Names must be 60 characters or fewer.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-primary px-6" data-testid="naming-screen">
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: 900,
          height: 600,
          background: "radial-gradient(closest-side, color-mix(in oklab, var(--color-accent) 18%, transparent), transparent 70%)",
          filter: "blur(10px)",
          zIndex: 0,
        }}
      />
      <form
        onSubmit={submit}
        className="relative w-full max-w-[520px] text-center"
        style={{ zIndex: 1 }}
      >
        <p
          className="font-mono text-text-tertiary mb-3"
          style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}
        >
          New project
        </p>
        <h1
          className="text-text-primary"
          style={{ fontSize: "clamp(32px, 4vw, 42px)", fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.1 }}
          data-testid="naming-headline"
        >
          Name your project
        </h1>
        <p className="text-text-secondary mt-3 mb-8" style={{ fontSize: 14 }}>
          You can rename this anytime.
        </p>

        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError(null);
          }}
          maxLength={60}
          placeholder="My Startup API"
          data-testid="project-name-input"
          className="outline-none w-full text-center motion-micro"
          style={{
            height: 56,
            padding: "0 20px",
            borderRadius: 12,
            background: "var(--color-background-secondary)",
            border: `1px solid ${error ? "var(--color-danger)" : "var(--color-border-primary)"}`,
            color: "var(--color-text-primary)",
            fontSize: 18,
            fontWeight: 500,
            letterSpacing: "-0.005em",
          }}
        />
        {error && (
          <p
            data-testid="naming-error"
            className="text-left mt-2"
            style={{ fontSize: 12, color: "var(--color-danger)" }}
          >
            {error}
          </p>
        )}
        <div className="mt-2 flex items-center justify-between" style={{ fontSize: 11 }}>
          <span></span>
          <span className="font-mono text-text-tertiary">{name.length}/60</span>
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="naming-submit"
          className="motion-micro inline-flex items-center justify-center gap-2 mt-8"
          style={{
            background: "var(--color-accent)",
            color: "#0a0a0a",
            height: 52,
            padding: "0 28px",
            borderRadius: 12,
            fontSize: 15,
            fontWeight: 600,
            border: "none",
            cursor: submitting ? "not-allowed" : "pointer",
            minWidth: 220,
            boxShadow:
              "0 1px 0 rgba(255,255,255,0.18) inset, 0 18px 40px -20px color-mix(in oklab, var(--color-accent) 65%, transparent)",
          }}
          onMouseEnter={(e) => !submitting && (e.currentTarget.style.background = "var(--color-accent-hover)")}
          onMouseLeave={(e) => !submitting && (e.currentTarget.style.background = "var(--color-accent)")}
        >
          {submitting ? "Starting…" : "Start Building"} <ArrowRight size={16} strokeWidth={2.25} />
        </button>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Phase 2 — Chat with the LLM until "ready"
 * ------------------------------------------------------------------ */
const TOPIC_LABELS = {
  use_case: "Use case",
  cloud_provider: "Cloud provider",
  scale: "Scale",
  budget: "Budget",
  stack: "Existing stack",
  compliance: "Compliance",
};

function TopicChips({ topics }) {
  const order = ["use_case", "cloud_provider", "scale", "budget", "stack", "compliance"];
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="topic-chips">
      {order.map((k) => {
        const covered = !!topics?.[k];
        return (
          <span
            key={k}
            data-testid={`topic-chip-${k}-${covered ? "covered" : "open"}`}
            className="font-mono motion-micro"
            style={{
              padding: "3px 9px",
              borderRadius: 9999,
              fontSize: 10,
              letterSpacing: "0.04em",
              color: covered ? "var(--color-accent)" : "var(--color-text-tertiary)",
              background: covered
                ? "color-mix(in oklab, var(--color-accent) 14%, transparent)"
                : "var(--color-background-tertiary)",
              border: `1px solid ${
                covered
                  ? "color-mix(in oklab, var(--color-accent) 45%, transparent)"
                  : "var(--color-border-secondary)"
              }`,
            }}
          >
            {covered ? "✓ " : ""}
            {TOPIC_LABELS[k]}
          </span>
        );
      })}
    </div>
  );
}

function ChatBubble({ turn }) {
  const isUser = turn.role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} fade-up`}
      data-testid={`chat-turn-${turn.role}`}
    >
      <div
        style={{
          maxWidth: "75%",
          background: isUser ? "var(--color-accent)" : "var(--color-background-secondary)",
          color: isUser ? "#0a0a0a" : "var(--color-text-primary)",
          border: isUser ? "none" : "1px solid var(--color-border-primary)",
          padding: "12px 16px",
          borderRadius: 14,
          fontSize: 14,
          lineHeight: 1.5,
          letterSpacing: "-0.005em",
          whiteSpace: "pre-wrap",
        }}
      >
        {turn.text}
      </div>
    </div>
  );
}

export function ChatScreen({ project, onLeave, onReady }) {
  const [conversation, setConversation] = useState(project.conversation || []);
  const [topics, setTopics] = useState(project.topics || {});
  const [ready, setReady] = useState(Boolean(project.ready));
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [savedTick, setSavedTick] = useState(0); // increments on each save to flash "Saved"
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Keep the latest message in view.
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [conversation.length]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    const now = new Date().toISOString();
    // Optimistic user bubble + typing indicator.
    setConversation((prev) => [...prev, { role: "user", text, ts: now }]);
    setSending(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/chat`, { message: text });
      setConversation((prev) => [...prev, { role: "assistant", text: data.reply, ts: new Date().toISOString() }]);
      setTopics(data.topics || {});
      setReady(Boolean(data.ready));
      setSavedTick((t) => t + 1);
      if (data.ready) {
        // Small beat so the user reads the confirmation message before the loader takes over.
        setTimeout(() => onReady(), 1200);
      }
    } catch (e) {
      toast.error("Couldn't send message", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
      // Roll back the optimistic user bubble on hard failure.
      setConversation((prev) => prev.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const allCovered = ready;

  return (
    <div className="min-h-screen flex flex-col bg-bg-primary" data-testid="chat-screen">
      <header
        className="border-b sticky top-0 z-30"
        style={{
          background: "rgba(10,10,10,0.92)",
          backdropFilter: "blur(12px)",
          borderColor: "var(--color-border-primary)",
        }}
      >
        <div className="max-w-[920px] mx-auto px-6 h-[64px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onLeave}
              data-testid="chat-back"
              className="motion-micro inline-flex items-center gap-1.5"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-secondary)",
                height: 34,
                padding: "0 12px",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--color-text-primary)";
                e.currentTarget.style.borderColor = "var(--color-accent)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--color-text-secondary)";
                e.currentTarget.style.borderColor = "var(--color-border-primary)";
              }}
            >
              <ArrowLeft size={14} /> Back to Dashboard
            </button>
            <Logo />
          </div>
          <div className="hidden md:flex items-center gap-3 min-w-0">
            <span
              className="text-text-primary truncate"
              style={{ fontSize: 14, fontWeight: 500, letterSpacing: "-0.005em" }}
              data-testid="chat-project-name"
            >
              {project.name}
            </span>
            <span
              className="font-mono"
              data-testid="chat-status"
              style={{
                padding: "3px 9px",
                borderRadius: 9999,
                fontSize: 10,
                letterSpacing: "0.08em",
                color: "var(--color-accent)",
                background: "color-mix(in oklab, var(--color-accent) 14%, transparent)",
                border: "1px solid color-mix(in oklab, var(--color-accent) 45%, transparent)",
                textTransform: "uppercase",
              }}
              key={savedTick}
            >
              {savedTick > 0 ? "Saved" : "Draft"}
            </span>
          </div>
        </div>
        <div
          className="border-t"
          style={{ borderColor: "var(--color-border-secondary)", background: "var(--color-background-primary)" }}
        >
          <div className="max-w-[920px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
            <span
              className="font-mono text-text-tertiary"
              style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              Topics covered
            </span>
            <TopicChips topics={topics} />
          </div>
        </div>
      </header>

      <main
        ref={scrollerRef}
        className="flex-1 overflow-y-auto"
        style={{ paddingBottom: 32 }}
      >
        <div className="max-w-[760px] mx-auto px-6 py-10 flex flex-col gap-5" data-testid="chat-list">
          {conversation.map((turn, i) => (
            <ChatBubble key={`${turn.role}-${i}`} turn={turn} />
          ))}
          {sending && (
            <div className="flex justify-start fade-up" data-testid="chat-typing">
              <div
                style={{
                  background: "var(--color-background-secondary)",
                  border: "1px solid var(--color-border-primary)",
                  padding: "12px 16px",
                  borderRadius: 14,
                  display: "inline-flex",
                  gap: 6,
                  alignItems: "center",
                }}
              >
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 9999,
                      background: "var(--color-text-tertiary)",
                      animation: `skeleton-shimmer 1s ease-in-out ${i * 0.15}s infinite`,
                      opacity: 0.6,
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <footer
        className="border-t"
        style={{
          background: "rgba(10,10,10,0.92)",
          backdropFilter: "blur(12px)",
          borderColor: "var(--color-border-primary)",
        }}
      >
        <div className="max-w-[760px] mx-auto px-6 py-4 flex items-end gap-3">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={allCovered ? "Generating shortly…" : "Type a message or answer the question above…"}
            disabled={sending || allCovered}
            data-testid="chat-input"
            rows={1}
            className="outline-none flex-1 motion-micro"
            style={{
              background: "var(--color-background-secondary)",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-primary)",
              padding: "12px 14px",
              borderRadius: 10,
              fontSize: 14,
              resize: "none",
              minHeight: 44,
              maxHeight: 140,
              lineHeight: 1.45,
            }}
          />
          <button
            type="button"
            onClick={send}
            disabled={!input.trim() || sending || allCovered}
            data-testid="chat-send"
            className="motion-micro inline-flex items-center justify-center gap-1.5"
            style={{
              background: "var(--color-accent)",
              color: "#0a0a0a",
              border: "none",
              height: 44,
              padding: "0 16px",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: !input.trim() || sending || allCovered ? "not-allowed" : "pointer",
              opacity: !input.trim() || sending || allCovered ? 0.55 : 1,
            }}
          >
            <Send size={14} strokeWidth={2.25} /> Send
          </button>
        </div>
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Phase 3 — Generation loader with cycling messages
 * ------------------------------------------------------------------ */
export function GeneratingScreen({ stage = null, stageKey = "queued", stageIndex = 0, totalStages = 0, startedAt }) {
  // Real stage labels come from the generation job (the backend reports one per wave of
  // the agent pipeline). Until the first poll lands we fall back to the rotating
  // placeholders, so the screen is never blank.
  const [idx, setIdx] = useState(0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (stage) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % LOADING_MESSAGES.length), 3000);
    return () => clearInterval(t);
  }, [stage]);
  useEffect(() => {
    const tick = () => setNow(Date.now());
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [startedAt]);

  const message = stage || LOADING_MESSAGES[idx];
  const hasSteps = totalStages > 0;
  const elapsed = Math.max(0, Math.floor((now - (startedAt || now)) / 1000));
  const elapsedLabel = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-primary px-6" data-testid="generating-screen">
      <div className="text-center" style={{ maxWidth: 480 }}>
        <div
          className="grid place-items-center mx-auto motion-canvas"
          style={{
            width: 80,
            height: 80,
            borderRadius: 9999,
            background: "color-mix(in oklab, var(--color-accent) 12%, transparent)",
            border: "1px solid color-mix(in oklab, var(--color-accent) 45%, transparent)",
            color: "var(--color-accent)",
          }}
        >
          <Sparkles size={28} strokeWidth={1.5} className="animate-pulse" />
        </div>
        <p
          key={message}
          className="text-text-primary fade-up mt-8"
          style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-0.015em" }}
          data-testid="generating-message"
        >
          {message}
        </p>
        {hasSteps && (
          <>
            <p className="text-text-secondary mt-2" style={{ fontSize: 13 }} data-testid="generating-step">
              Step {Math.min(stageIndex + 1, totalStages)} of {totalStages}
            </p>
            <ol className="mt-6 text-left space-y-2" aria-label="Generation stages">
              {GENERATION_STAGES.slice(0, totalStages).map((item, index) => {
                const active = stageKey === item.key ||
                  (stageKey === "queued" && stage && index === stageIndex);
                const complete = stageKey === "complete" || (stageKey !== "queued" && index < stageIndex);
                return (
                  <li key={item.key} data-testid={`generation-stage-${item.key}`}
                    aria-current={active ? "step" : undefined}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${
                      active ? "bg-bg-secondary text-text-primary" :
                      complete ? "text-text-secondary" : "text-text-tertiary"
                    }`}>
                    <span aria-hidden="true" className="w-5 text-center font-mono">
                      {complete ? "✓" : index + 1}
                    </span>
                    <span>{item.label}</span>
                  </li>
                );
              })}
            </ol>
          </>
        )}
        <div className="progress-indeterminate mt-8 mx-auto" style={{ maxWidth: 280 }} />
        <p className="text-text-secondary mt-5 font-mono" style={{ fontSize: 12 }} data-testid="generation-elapsed">
          Elapsed {elapsedLabel}
        </p>
        <p className="text-text-tertiary mt-2" style={{ fontSize: 12 }}>
          A team of specialist agents is designing this. It may take several minutes.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Wrapper — orchestrates the three phases
 * ------------------------------------------------------------------ */
export default function NewProject() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("template");
  const provider = searchParams.get("provider");
  const [phase, setPhase] = useState("naming"); // naming | chat | generating
  const [project, setProject] = useState(null);
  const [progress, setProgress] = useState({ stage: null, stageKey: "queued", stageIndex: 0, totalStages: 0 });
  const [generationStartedAt, setGenerationStartedAt] = useState(null);
  const [runLimitOpen, setRunLimitOpen] = useState(false);

  const createDraft = async (name) => {
    try {
      const { data } = await api.post("/projects/draft", {
        name,
        template_id: templateId || undefined,
        provider: provider || undefined,
      });
      setProject(data);
      setPhase("chat");
    } catch (e) {
      toast.error("Couldn't create draft", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  };

  // Generation runs as a background job and can take several minutes. We start the job,
  // then poll it for per-wave progress. `cancelled` stops the loop if the user
  // navigates away mid-generation.
  const cancelled = useRef(false);
  useEffect(() => () => { cancelled.current = true; }, []);

  const startGenerate = async () => {
    if (!project) return;
    setGenerationStartedAt(Date.now());
    setPhase("generating");
    setProgress({ stage: null, stageKey: "queued", stageIndex: 0, totalStages: 0 });

    const fail = (e) => {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (status === 429) {
        setRunLimitOpen(true);
      } else {
        toast.error("Generation failed", { description: detail });
      }
      setPhase("chat");
    };

    let jobId;
    try {
      const { data } = await api.post(`/projects/${project.id}/generate-from-chat/async`);
      jobId = data.job_id;
    } catch (e) {
      fail(e);
      return;
    }

    // Poll until the job reaches a terminal state. The backend fails an abandoned job
    // rather than leaving it running forever, so this loop always terminates.
    while (!cancelled.current) {
      await new Promise((r) => setTimeout(r, GENERATION_POLL_MS));
      if (cancelled.current) return;

      let job;
      try {
        ({ data: job } = await api.get(`/generation-jobs/${jobId}`));
      } catch (e) {
        fail(e);
        return;
      }

      setProgress({
        stage: job.stage_label || null,
        stageKey: job.stage || "queued",
        stageIndex: job.stage_index || 0,
        totalStages: job.total_stages || 0,
      });

      if (job.status === "done") {
        const architecture = job.result?.architecture || [];
        toast.success("Architecture ready", { description: `${architecture.length} services` });
        navigate(`/projects/${job.result.id}/output`);
        return;
      }
      if (job.status === "failed") {
        toast.error("Generation failed", { description: job.error || "Please try again." });
        setPhase("chat");
        return;
      }
    }
  };

  let content;
  if (phase === "naming") {
    content = <NamingScreen onSubmit={createDraft} prefillFromTemplate={templateId ? "" : null} />;
  } else if (phase === "chat" && project) {
    content = (
      <ChatScreen
        project={project}
        onLeave={() => navigate("/dashboard")}
        onReady={startGenerate}
      />
    );
  } else {
    content = <GeneratingScreen
      stage={progress.stage}
      stageKey={progress.stageKey}
      stageIndex={progress.stageIndex}
      totalStages={progress.totalStages}
      startedAt={generationStartedAt}
    />;
  }
  return (
    <>
      {content}
      <RunLimitDialog open={runLimitOpen} onClose={() => setRunLimitOpen(false)} />
    </>
  );
}
